<template>
    <div>

        <b-breadcrumb>
            <b-breadcrumb-item to="/">
                <i class="fa fa-home"></i>
                Home
            </b-breadcrumb-item>
            <b-breadcrumb-item active>Organizations</b-breadcrumb-item>
        </b-breadcrumb>

        <div class="card">
            <div class="bg-dark card-header text-white">Organizations</div>
            <div class="card-body">
                <!-- The collapsable filter options -->
                <AppFormCardCollapsable :display-footer="true" @submit.prevent="onFilterSubmit()" class="mb-3">
                    <template v-slot:header>Filter</template>
                    <template v-slot:body>
                        <div class="form-row">
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Name</label>
                                <input
                                        class="form-control"
                                        placeholder="Name"
                                        v-model="filterOptions.name"
                                />
                            </div>
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Code</label>
                                <input
                                        class="form-control"
                                        placeholder="Short Code"
                                        v-model="filterOptions.code"
                                />
                            </div>
                            <div class="col-md-4 col-sm-6 form-group">
                                <label>Organization Type</label>
                                <select class="form-control" v-model="filterOptions.type">
                                    <option :value="null">-- Select Organization Type --</option>
                                    <option>Group</option>
                                    <option>Division</option>
                                    <option>Program</option>
                                </select>
                            </div>
                        </div>
                        <div class="form-row">
                            <div class="col-md-2 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowActive"
                                >Only Show Active
                                </AppCheckBox>
                            </div>
                            <div class="col-md-2 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowWithPeople"
                                >People Indicator
                                </AppCheckBox>
                            </div>
                            <div class="col-md-2 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowMissingAddress"
                                >Only Show Missing Address
                                </AppCheckBox>
                            </div>
                            <div class="col-md-3 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowMissingParentOrganization"
                                >Only Show Missing Parent Organization
                                </AppCheckBox>
                            </div>
                            <div class="col-md-3 form-group">
                                <AppCheckBox
                                        v-model="filterOptions.onlyShowMissingProgramManager"
                                >Only Show Missing Program Manager
                                </AppCheckBox>
                            </div>
                        </div>
                    </template>
                    <template v-slot:footer>
                        <button class="btn btn-primary" type="submit">Apply</button>
                    </template>
                </AppFormCardCollapsable>

                <div class="row">
                    <div class="col d-print-none dropdown mb-3 text-right">
                        <button :disabled="isPerformingAction" aria-expanded="false" aria-haspopup="true"
                                class="btn btn-secondary dropdown-toggle" data-toggle="dropdown" style="width: 100px"
                                type="button">
                            <span>Actions</span>
                        </button>
                        <div class="dropdown-menu">
                            <a @click.prevent="downloadBusinessRolesExport(true)" class="btn btn-outline-light dropdown-item text-left"
                              href="javascript:void(0)" title="Export Business Roles to Excel">
                                <i class="fa fa-file-excel"></i>Download Business Roles as Excel
                            </a>
                            <a @click.prevent="downloadBusinessRolesExport(false)" class="btn btn-outline-light dropdown-item text-left"
                              href="javascript:void(0)" title="Export Business Roles to PDF">
                                <i class="fa fa-file-pdf"></i>Download Business Roles as PDF
                            </a>
                        </div>
                    </div>
                </div>

                <BTable
                        :busy="isLoading"
                        :fields="tableFields"
                        :items="organizations"
                        :responsive="true"
                        primary-key="id"
                        show-empty
                        striped
                        table-class="table-align-middle">

                    <template v-slot:cell(id)="row">
                        <router-link
                                :to="{name:'OrganizationDetails',params:{id:row.item.id,organizationName: row.item.preferredName === null ? row.item.name : row.item.preferredName,organizationCode: row.item.code}}"
                                class="btn btn-sm btn-warning d-print-none"
                                data-toggle="tooltip"
                                title="Edit organization details">
                            <i class="fa fa-pencil-alt"></i>Edit
                        </router-link>
                    </template>

                    <template v-slot:cell(name)="row">
                        {{ row.item.preferredName === null ? row.item.name : row.item.preferredName }}
                    </template>

                    <template v-slot:cell(isActive)="row">
                        <AppDisplayTemplateBoolean :value="row.item.isActive"></AppDisplayTemplateBoolean>
                    </template>

                    <template v-slot:cell(peopleIndicator)="row">
                        <AppDisplayTemplateBoolean :value="row.item.peopleIndicator"></AppDisplayTemplateBoolean>
                    </template>

                    <!-- Footer Pagination -->
                    <template v-slot:custom-foot>
                        <b-tr>
                            <b-td :colspan="tableFields.length">
                                <AppTablePagination
                                        :page.sync="filterOptions.page"
                                        :pageSize.sync="filterOptions.pageSize"
                                        :totalCount.sync="totalOrganizations"
                                        v-on:update:page="loadData()"
                                        v-on:update:pageSize="loadData()"
                                ></AppTablePagination>
                            </b-td>
                        </b-tr>
                    </template>

                </BTable>
            </div>
        </div>

    </div>
</template>

<script>
    import OrganizationService from "@/services/OrganizationService";
    import AppFormCardCollapsable from "@/components/shared/AppFormCardCollapsable";
    import AppCheckBox from "@/components/shared/AppCheckBox";
    import AppTablePagination from "@/components/shared/AppTablePagination";
    import AppDisplayTemplateBoolean from "@/components/shared/AppDisplayTemplateBoolean";
    import {ToastType} from "@/services/models/shared/ToastType";

    export default {
        name: "OrganizationList",
        components: {
            AppDisplayTemplateBoolean,
            AppFormCardCollapsable,
            AppCheckBox,
            AppTablePagination
        },
        data() {
            return {
                organizations: [],
                totalOrganizations: 0,
                filterOptions: {
                    returnAll: false,
                    page: 1,
                    pageSize: 10,
                    code: null,
                    name: null,
                    type: null,
                    onlyShowActive: true,
                    onlyShowMissingAddress: false,
                    onlyShowMissingParentOrganization: false,
                    onlyShowMissingProgramManager: false,
                    onlyShowWithPeople: true
                },
                isLoading: false,
                tableFields: [
                    {
                        key: 'id',
                        label: '',
                        thStyle: 'width:100px'
                    },
                    {
                        key: 'code',
                        label: 'Code',
                        thStyle: 'width:100px'
                    },
                    {
                        key: 'name',
                        label: 'Name',
                    },
                    {
                        key: 'type',
                        label: 'Type',
                    },
                    {
                        key: 'programManagerName',
                        label: 'Program Manager',
                    },
                    {
                        key: 'parentOrganizationCode',
                        label: 'Parent Organization',
                        thStyle: 'width:200px'
                    },
                    {
                        key: 'peopleIndicator',
                        label: 'People Indicator',
                        thStyle: 'width:100px',
                        thClass: 'text-center',
                        tdClass: 'text-center'
                    },
                    {
                        key: 'isActive',
                        label: 'Is Active',
                        thStyle: 'width:100px',
                        thClass: 'text-center',
                        tdClass: 'text-center'
                    }
                ]
            }
        },

        methods: {
            /**
             * @description Loads the data from the API with the filter options bound to the component
             */
            loadData() {
                const vm = this;
                vm.isLoading = true;

                OrganizationService.getOrganizationList(vm.filterOptions)
                    .then(response => {
                        vm.organizations = response.data;
                        vm.totalOrganizations = response.totalCount;
                    }).catch(() => {
                    vm.redirectToError()
                }).finally(() => {
                    vm.isLoading = false;
                })
            },

            /**
             * @description Resets the page to the first page and reloads the data
             */
            onFilterSubmit() {
                this.filterOptions.page = 1;
                this.loadData();
            },

            /**
             * @description Downloads the excel export of the organizations' business roles
             */
            downloadBusinessRolesExport(asExcel) {
                const vm = this;

                OrganizationService.getExcelExport(asExcel)
                    .catch(error => {
                        vm.alert(ToastType.Danger, "Unable to download report")
                    });
            },
        },

        created() {
            this.loadData();
        }
    }
</script>
